import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Modal } from "../../components/Modal/Modal";
import { Button } from "../../components/Button/Button";
import { Alert } from "../../components/Alert/Alert";
import { useToast } from "../../components/Toast/Toast";
import { ApiError, describeError } from "../../lib/errors";

import { AdminUser, promoteUser } from "./api";
import { UserPreviewCard } from "./UserPreviewCard";
import { RoleArrow } from "./RoleArrow";
import styles from "./UsersPage.module.css";

interface Props {
  open: boolean;
  user: AdminUser | null;
  onClose: () => void;
}

/**
 * Promote-to-Super-admin confirmation modal. Mockup L501-L526.
 *
 * Shows a user-preview card, a Member → Super admin role-arrow visual, an
 * info Alert enumerating the new super-admin powers, and a reassurance line
 * about kept project memberships.
 */
export function PromoteModal({ open, user, onClose }: Props) {
  const qc = useQueryClient();
  const { push } = useToast();

  const mut = useMutation({
    mutationFn: (id: string) => promoteUser(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      push({
        variant: "success",
        message: "User promoted to super admin.",
      });
      onClose();
    },
    onError: (err: unknown) => {
      push({
        variant: "danger",
        message:
          err instanceof ApiError
            ? describeError(err.code)
            : describeError("internal"),
      });
    },
  });

  if (!user) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Promote to Super admin?"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={mut.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => mut.mutate(user.id)}
            disabled={mut.isPending}
            loading={mut.isPending}
          >
            Promote to Super admin
          </Button>
        </>
      }
    >
      <UserPreviewCard user={user} />
      <RoleArrow from="project_user" to="super_admin" />
      <Alert variant="info">
        Super admins have platform-wide access to:
        <ul className={styles.alertList}>
          <li>All projects (config, team, keys)</li>
          <li>Platform users (invite, promote, disable)</li>
          <li>Audit logs</li>
        </ul>
      </Alert>
      <p className={styles.modalFootnote}>
        Project memberships are kept — if demoted later, this user keeps their
        project access.
      </p>
    </Modal>
  );
}
